import { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Container,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  VStack,
  Spinner,
  Alert,
  AlertIcon,
  HStack,
  FormControl,
  Input,
  Button,
  SimpleGrid,
  InputGroup,
  InputLeftElement,
  Text,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Select,
  Badge,
  Link,
} from "@chakra-ui/react";

import { API_BASE } from "./config";
import {
  CopyIcon,
  SearchIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ExternalLinkIcon,
} from "@chakra-ui/icons";
import { Pagination } from "./components/Pagination";
import toast from "react-hot-toast";

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
};

interface FeeTransaction {
  id: string;
  userId: string;
  walletPublicAddress: string;
  transactionHash: string;
  blockNumber: number;
  ethAmount: number;
  timestamp: number;
  date: string;
  processed: boolean;
}

interface PaginationData {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  limit: number;
}

interface Statistics {
  totalEth: number;
  averageEth: number;
}

function FeeTransactions() {
  const [transactions, setTransactions] = useState<FeeTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "asc" | "desc";
  }>({ key: "timestamp", direction: "desc" });

  // Filter states
  const [filters, setFilters] = useState({
    userId: "",
    walletAddress: "",
    transactionHash: "",
    fromBlock: "",
    toBlock: "",
    minAmount: "",
    maxAmount: "",
    startDate: "",
    endDate: "",
    processed: "",
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [pagination, setPagination] = useState<PaginationData>({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 20,
  });
  const [statistics, setStatistics] = useState<Statistics>({
    totalEth: 0,
    averageEth: 0,
  });

  const fetchTransactions = async () => {
    setLoading(true);
    setError("");
    try {
      const params: any = {
        page: currentPage,
        limit: itemsPerPage,
        sortBy: sortConfig.key,
        sortOrder: sortConfig.direction,
      };

      // Add filters if they have values
      if (filters.userId) params.userId = filters.userId;
      if (filters.walletAddress) params.walletAddress = filters.walletAddress;
      if (filters.transactionHash)
        params.transactionHash = filters.transactionHash;
      if (filters.fromBlock) params.fromBlock = parseInt(filters.fromBlock);
      if (filters.toBlock) params.toBlock = parseInt(filters.toBlock);
      if (filters.minAmount) params.minAmount = parseFloat(filters.minAmount);
      if (filters.maxAmount) params.maxAmount = parseFloat(filters.maxAmount);
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      if (filters.processed !== "")
        params.processed = filters.processed === "true";

      const res = await axios.get(
        `${API_BASE}/api/admin/fees/transactions`,
        {
          params,
        }
      );

      if (res.data.success) {
        setTransactions(res.data.data || []);
        setPagination(
          res.data.pagination || {
            currentPage: 1,
            totalPages: 1,
            totalCount: 0,
            limit: itemsPerPage,
          }
        );
        setStatistics(
          res.data.statistics || {
            totalEth: 0,
            averageEth: 0,
          }
        );
      }
    } catch (err: any) {
      setError(
        err?.response?.data?.message || "Error loading transactions data"
      );
      toast.error(
        err?.response?.data?.message || "Error loading transactions data"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, [currentPage, itemsPerPage, sortConfig]);

  const handleSort = (key: string) => {
    let direction: "asc" | "desc" = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
    setCurrentPage(1);
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchTransactions();
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      userId: "",
      walletAddress: "",
      transactionHash: "",
      fromBlock: "",
      toBlock: "",
      minAmount: "",
      maxAmount: "",
      startDate: "",
      endDate: "",
      processed: "",
    };
    setFilters(clearedFilters);
    setCurrentPage(1);

    // Immediately fetch with cleared filters
    setLoading(true);
    setError("");

    const params: any = {
      page: 1,
      limit: itemsPerPage,
      sortBy: sortConfig.key,
      sortOrder: sortConfig.direction,
    };

    axios.get(`${API_BASE}/api/admin/fees/transactions`, { params })
      .then((res) => {
        if (res.data.success) {
          setTransactions(res.data.data || []);
          setPagination(
            res.data.pagination || {
              currentPage: 1,
              totalPages: 1,
              totalCount: 0,
              limit: itemsPerPage,
            }
          );
          setStatistics(
            res.data.statistics || {
              totalEth: 0,
              averageEth: 0,
            }
          );
        }
      })
      .catch((err: any) => {
        setError(
          err?.response?.data?.message || "Error loading transactions data"
        );
        toast.error(
          err?.response?.data?.message || "Error loading transactions data"
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const renderSortIcon = (key: string) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === "asc" ? (
      <ChevronUpIcon ml={1} />
    ) : (
      <ChevronDownIcon ml={1} />
    );
  };

  return (
    <Container maxW="container.xl" py={8}>
      <VStack spacing={8} align="stretch">
        <Heading as="h1" size="xl" textAlign="center" color="white">
          Fee Transactions
        </Heading>

        {/* Statistics Cards */}
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
          <Box
            bg="gray.800"
            p={6}
            borderRadius="lg"
            borderWidth="1px"
            borderColor="gray.700"
          >
            <Stat>
              <StatLabel color="gray.400">Total ETH Collected</StatLabel>
              <StatNumber color="brand.400" fontSize="3xl">
                {statistics.totalEth.toFixed(6)} ETH
              </StatNumber>
              <StatHelpText color="gray.500">
                Total from {pagination.totalCount} transactions
              </StatHelpText>
            </Stat>
          </Box>
          <Box
            bg="gray.800"
            p={6}
            borderRadius="lg"
            borderWidth="1px"
            borderColor="gray.700"
          >
            <Stat>
              <StatLabel color="gray.400">Average ETH Per Transaction</StatLabel>
              <StatNumber color="brand.400" fontSize="3xl">
                {statistics.averageEth.toFixed(6)} ETH
              </StatNumber>
              <StatHelpText color="gray.500">
                Average across all transactions
              </StatHelpText>
            </Stat>
          </Box>
        </SimpleGrid>

        {/* Filters */}
        <Box
          bg="gray.800"
          p={6}
          borderRadius="lg"
          borderWidth="1px"
          borderColor="gray.700"
        >
          <VStack spacing={4} align="stretch">
            {/* Row 1: User ID, Wallet Address, Transaction Hash */}
            <HStack spacing={4} flexWrap="wrap">
              <InputGroup maxW="250px">
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.300" />
                </InputLeftElement>
                <Input
                  placeholder="User ID"
                  value={filters.userId}
                  onChange={(e) =>
                    setFilters({ ...filters, userId: e.target.value })
                  }
                  bg="gray.900"
                  borderColor="gray.600"
                  color="white"
                  _placeholder={{ color: "gray.500" }}
                />
              </InputGroup>

              <InputGroup maxW="250px">
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.300" />
                </InputLeftElement>
                <Input
                  placeholder="Wallet Address"
                  value={filters.walletAddress}
                  onChange={(e) =>
                    setFilters({ ...filters, walletAddress: e.target.value })
                  }
                  bg="gray.900"
                  borderColor="gray.600"
                  color="white"
                  _placeholder={{ color: "gray.500" }}
                />
              </InputGroup>

              <InputGroup maxW="250px">
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.300" />
                </InputLeftElement>
                <Input
                  placeholder="Transaction Hash"
                  value={filters.transactionHash}
                  onChange={(e) =>
                    setFilters({
                      ...filters,
                      transactionHash: e.target.value,
                    })
                  }
                  bg="gray.900"
                  borderColor="gray.600"
                  color="white"
                  _placeholder={{ color: "gray.500" }}
                />
              </InputGroup>
            </HStack>

            {/* Row 2: Block Range */}
            <HStack spacing={4} flexWrap="wrap">
              <FormControl maxW="150px">
                <Input
                  type="number"
                  placeholder="From Block"
                  value={filters.fromBlock}
                  onChange={(e) =>
                    setFilters({ ...filters, fromBlock: e.target.value })
                  }
                  bg="gray.900"
                  borderColor="gray.600"
                  color="white"
                  _placeholder={{ color: "gray.500" }}
                />
              </FormControl>

              <FormControl maxW="150px">
                <Input
                  type="number"
                  placeholder="To Block"
                  value={filters.toBlock}
                  onChange={(e) =>
                    setFilters({ ...filters, toBlock: e.target.value })
                  }
                  bg="gray.900"
                  borderColor="gray.600"
                  color="white"
                  _placeholder={{ color: "gray.500" }}
                />
              </FormControl>

              <FormControl maxW="150px">
                <Input
                  type="number"
                  placeholder="Min Amount"
                  value={filters.minAmount}
                  onChange={(e) =>
                    setFilters({ ...filters, minAmount: e.target.value })
                  }
                  bg="gray.900"
                  borderColor="gray.600"
                  color="white"
                  _placeholder={{ color: "gray.500" }}
                  step="0.000001"
                />
              </FormControl>

              <FormControl maxW="150px">
                <Input
                  type="number"
                  placeholder="Max Amount"
                  value={filters.maxAmount}
                  onChange={(e) =>
                    setFilters({ ...filters, maxAmount: e.target.value })
                  }
                  bg="gray.900"
                  borderColor="gray.600"
                  color="white"
                  _placeholder={{ color: "gray.500" }}
                  step="0.000001"
                />
              </FormControl>
            </HStack>

            {/* Row 3: Date Range and Processed Status */}
            <HStack spacing={4} flexWrap="wrap">
              <FormControl maxW="180px">
                <Input
                  type="date"
                  placeholder="Start Date"
                  value={filters.startDate}
                  onChange={(e) =>
                    setFilters({ ...filters, startDate: e.target.value })
                  }
                  bg="gray.900"
                  borderColor="gray.600"
                  color="white"
                  _placeholder={{ color: "gray.500" }}
                />
              </FormControl>

              <FormControl maxW="180px">
                <Input
                  type="date"
                  placeholder="End Date"
                  value={filters.endDate}
                  onChange={(e) =>
                    setFilters({ ...filters, endDate: e.target.value })
                  }
                  bg="gray.900"
                  borderColor="gray.600"
                  color="white"
                  _placeholder={{ color: "gray.500" }}
                />
              </FormControl>

              <FormControl maxW="180px">
                <Select
                  placeholder="Processed Status"
                  value={filters.processed}
                  onChange={(e) =>
                    setFilters({ ...filters, processed: e.target.value })
                  }
                  bg="gray.900"
                  borderColor="gray.600"
                  color="white"
                >
                  <option value="true">Processed</option>
                  <option value="false">Not Processed</option>
                </Select>
              </FormControl>

              <Button colorScheme="brand" onClick={handleSearch} size="md">
                Apply Filters
              </Button>

              <Button
                variant="outline"
                onClick={handleClearFilters}
                size="md"
                borderColor="gray.600"
                color="gray.300"
                _hover={{ bg: "gray.700" }}
              >
                Clear
              </Button>
            </HStack>
          </VStack>
        </Box>

        {error && (
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            {error}
          </Alert>
        )}

        {loading ? (
          <Box textAlign="center" py={10}>
            <Spinner size="xl" color="brand.500" />
          </Box>
        ) : (
          <>
            <Box
              overflowX="auto"
              bg="gray.800"
              borderRadius="lg"
              borderWidth="1px"
              borderColor="gray.700"
            >
              <Table variant="simple" colorScheme="gray">
                <Thead bg="gray.900">
                  <Tr>
                    <Th color="gray.400" borderColor="gray.700">
                      #
                    </Th>
                    <Th color="gray.400" borderColor="gray.700">
                      User ID
                    </Th>
                    <Th color="gray.400" borderColor="gray.700">
                      Wallet Address
                    </Th>
                    <Th color="gray.400" borderColor="gray.700">
                      Transaction Hash
                    </Th>
                    <Th
                      color="gray.400"
                      borderColor="gray.700"
                      cursor="pointer"
                      onClick={() => handleSort("blockNumber")}
                      _hover={{ color: "white" }}
                    >
                      <HStack spacing={1}>
                        <Text>Block Number</Text>
                        {renderSortIcon("blockNumber")}
                      </HStack>
                    </Th>
                    <Th
                      color="gray.400"
                      borderColor="gray.700"
                      cursor="pointer"
                      onClick={() => handleSort("ethAmount")}
                      _hover={{ color: "white" }}
                    >
                      <HStack spacing={1}>
                        <Text>ETH Amount</Text>
                        {renderSortIcon("ethAmount")}
                      </HStack>
                    </Th>
                    <Th
                      color="gray.400"
                      borderColor="gray.700"
                      cursor="pointer"
                      onClick={() => handleSort("timestamp")}
                      _hover={{ color: "white" }}
                    >
                      <HStack spacing={1}>
                        <Text>Date</Text>
                        {renderSortIcon("timestamp")}
                      </HStack>
                    </Th>
                    <Th
                      color="gray.400"
                      borderColor="gray.700"
                      cursor="pointer"
                      onClick={() => handleSort("processed")}
                      _hover={{ color: "white" }}
                    >
                      <HStack spacing={1}>
                        <Text>Status</Text>
                        {renderSortIcon("processed")}
                      </HStack>
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {transactions.length === 0 ? (
                    <Tr>
                      <Td
                        colSpan={8}
                        textAlign="center"
                        color="gray.500"
                        py={10}
                      >
                        No transactions found
                      </Td>
                    </Tr>
                  ) : (
                    transactions.map((tx, index) => (
                      <Tr key={tx.id} _hover={{ bg: "gray.750" }}>
                        <Td color="gray.300" borderColor="gray.700">
                          {(currentPage - 1) * itemsPerPage + index + 1}
                        </Td>
                        <Td color="gray.300" borderColor="gray.700">
                          <HStack>
                            <Text fontSize="sm">
                              {tx.userId.length > 12
                                ? `${tx.userId.slice(0, 6)}...${tx.userId.slice(-4)}`
                                : tx.userId
                              }
                            </Text>
                            <CopyIcon
                              cursor="pointer"
                              onClick={() => handleCopy(tx.userId)}
                              _hover={{ color: "brand.400" }}
                            />
                          </HStack>
                        </Td>
                        <Td
                          color="gray.300"
                          borderColor="gray.700"
                          fontFamily="mono"
                          fontSize="sm"
                        >
                          <HStack>
                            <Text>
                              {tx.walletPublicAddress.slice(0, 6)}...
                              {tx.walletPublicAddress.slice(-4)}
                            </Text>
                            <CopyIcon
                              cursor="pointer"
                              onClick={() =>
                                handleCopy(tx.walletPublicAddress)
                              }
                              _hover={{ color: "brand.400" }}
                            />
                          </HStack>
                        </Td>
                        <Td
                          color="gray.300"
                          borderColor="gray.700"
                          fontFamily="mono"
                          fontSize="sm"
                        >
                          <HStack>
                            <Link
                              href={`https://arbiscan.io/tx/${tx.transactionHash}`}
                              isExternal
                              color="brand.300"
                              _hover={{ color: "brand.400", textDecoration: "underline" }}
                            >
                              <HStack spacing={1}>
                                <Text>
                                  {tx.transactionHash.slice(0, 6)}...
                                  {tx.transactionHash.slice(-4)}
                                </Text>
                                <ExternalLinkIcon fontSize="xs" />
                              </HStack>
                            </Link>
                            <CopyIcon
                              cursor="pointer"
                              onClick={() => handleCopy(tx.transactionHash)}
                              _hover={{ color: "brand.400" }}
                            />
                          </HStack>
                        </Td>
                        <Td
                          color="gray.300"
                          borderColor="gray.700"
                          fontFamily="mono"
                        >
                          {tx.blockNumber.toLocaleString()}
                        </Td>
                        <Td
                          color="brand.300"
                          borderColor="gray.700"
                          fontWeight="bold"
                        >
                          {tx.ethAmount.toFixed(6)} ETH
                        </Td>
                        <Td
                          color="gray.300"
                          borderColor="gray.700"
                          fontSize="sm"
                        >
                          {formatDate(tx.date)}
                        </Td>
                        <Td color="gray.300" borderColor="gray.700">
                          <Badge
                            colorScheme={tx.processed ? "green" : "yellow"}
                          >
                            {tx.processed ? "Processed" : "Pending"}
                          </Badge>
                        </Td>
                      </Tr>
                    ))
                  )}
                </Tbody>
              </Table>
            </Box>

            {/* Pagination */}
            {pagination.totalCount > 0 && (
              <Pagination
                currentPage={currentPage}
                totalPages={pagination.totalPages}
                itemsPerPage={itemsPerPage}
                totalItems={pagination.totalCount}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={(items) => {
                  setItemsPerPage(items);
                  setCurrentPage(1);
                }}
              />
            )}
          </>
        )}
      </VStack>
    </Container>
  );
}

export default FeeTransactions;
